import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";
import * as XLSX from "xlsx";

export default function TwosAndFines() {
  const [players, setPlayers] = useState([]);
  const [scores, setScores] = useState([]);
  const [event, setEvent] = useState(null);
  const [entryFee, setEntryFee] = useState(3);
  const [entryFeeInput, setEntryFeeInput] = useState("3");
  const [paid, setPaid] = useState({});
  const [groupFines, setGroupFines] = useState({});

  useEffect(() => {
    api.get("/events/current").then(res => {
      if (res.data) {
        setEvent(res.data);
        api.get(`/events/${res.data.id}/players`).then(r => {
          const playing = r.data.filter(p => p.playing);
          setPlayers(playing);
        });
        api.get(`/scores/${res.data.id}`).then(r => {
          setScores(r.data);
        });
      }
    }).catch(() => {});
  }, []);

  // Find players who scored a 2 on any hole
  const twos = players.filter(p => scores.some(s => s.player_id === p.player_id && s.gross === 2));

  const paidCount = twos.filter(p => paid[p.player_id]).length;
  const twosTotal = paidCount * entryFee;

  const groupMap = {};
  players.forEach(p => { const g = p.grp || 0; if (!groupMap[g]) groupMap[g] = []; groupMap[g].push(p); });
  const sortedGroupNumbers = Object.keys(groupMap).map(Number).sort((a, b) => a - b);

  const totalFines = sortedGroupNumbers.reduce((sum, g) => sum + (groupFines[g]?.total || 0), 0);

  const handleGroupFineChange = (grp, val) => {
    const n = Math.max(0, parseFloat(val) || 0);
    setGroupFines(prev => ({ ...prev, [grp]: { total: n } }));
  };

  const clearTwos = () => { if (!window.confirm("Clear all Twos paid status?")) return; setPaid({}); };
  const clearFines = () => {
    if (!window.confirm("Clear all Fines totals?")) return;
    setGroupFines({});
  };

  const exportToExcel = () => {
    const twosData = twos.map(p => ({
      Player: p.name,
      Group: p.grp || "",
      Paid: paid[p.player_id] ? "Yes" : "No"
    }));
    twosData.push({ Player: "TOTAL", Group: "", Paid: `£${twosTotal.toFixed(2)} (${paidCount} / ${twos.length} paid)` });

    const finesData = sortedGroupNumbers.map(g => {
      const f = groupFines[g] || { total: 0 };
      return {
        Group: g || "-",
        Members: groupMap[g].map(p => p.name).join(", "),
        "Total Fines": `£${(f.total || 0).toFixed(2)}`
      };
    });
    finesData.push({ Group: "TOTAL", Members: "", "Total Fines": `£${totalFines.toFixed(2)}` });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(twosData), "Twos");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(finesData), "Fines");
    XLSX.writeFile(wb, "TwosAndFines.xlsx");
  };

  const printSection = (section) => {
    const w = window.open("", "_blank");
    let html = `<html><head><title>Print</title><style>body{font-family:Arial;font-size:13px;}table{border-collapse:collapse;width:100%;}th,td{border:1px solid #ccc;padding:6px 10px;}th{background:#14532d;color:white;}</style></head><body>`;
    if (section === "twos") {
      html += `<h2>Twos - Entry Fee: £${entryFee}</h2><table><tr><th>Player</th><th>Group</th><th>Paid £${entryFee}</th></tr>`;
      twos.forEach(p => { html += `<tr><td>${p.name}</td><td>${p.grp || ""}</td><td>${paid[p.player_id] ? "Yes" : "No"}</td></tr>`; });
      html += `</table><p>Paid: ${paidCount} / ${twos.length} &nbsp; Total: £${twosTotal.toFixed(2)}</p>`;
    } else {
      html += `<h2>Fines</h2><table><tr><th>Group</th><th>Members</th><th>Total Fines</th></tr>`;
      sortedGroupNumbers.forEach(g => {
        const f = groupFines[g] || { total: 0 };
        html += `<tr><td>${g || "-"}</td><td>${groupMap[g].map(p => p.name).join(", ")}</td><td>£${(f.total || 0).toFixed(2)}</td></tr>`;
      });
      html += `</table><p>Total Fines: £${totalFines.toFixed(2)}</p>`;
    }
    html += `</body></html>`;
    w.document.write(html);
    w.document.close();
    w.print();
  };

  const hdr = { backgroundColor: "#14532d", color: "white", padding: "8px 12px", fontWeight: "600", fontSize: "13px" };
  const row = (i) => ({ backgroundColor: i % 2 === 0 ? "#f0fdf4" : "white", padding: "6px 12px", fontSize: "13px", borderBottom: "1px solid #e5e7eb" });

  return (
    <Layout>
      <div style={{ padding: "16px", height: "100%", overflowY: "auto" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: "700", color: "#14532d" }}>💰 Twos & Fines</h2>
          <button onClick={exportToExcel} style={{ backgroundColor: "#16a34a", color: "white", border: "none", padding: "8px 16px", borderRadius: "6px", fontWeight: "600", cursor: "pointer", fontSize: "13px" }}>
            📊 Export to Excel
          </button>
        </div>

        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>

          {/* TWOS */}
          <div style={{ flex: 1, minWidth: "300px", border: "1px solid #d1d5db", borderRadius: "8px", overflow: "hidden" }}>
            <div style={{ backgroundColor: "#f0fdf4", padding: "10px 12px", borderBottom: "1px solid #d1d5db", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
              <div style={{ fontWeight: "700", fontSize: "13px" }}>🏌 TWOS — £{entryFee} per player</div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: "13px" }}>Entry fee: £</span>
                <input type="number" value={entryFeeInput} onChange={e => setEntryFeeInput(e.target.value)}
                  style={{ width: "60px", padding: "4px 6px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "13px" }} />
                <button onClick={() => setEntryFee(parseFloat(entryFeeInput) || 0)}
                  style={{ backgroundColor: "#2563eb", color: "white", border: "none", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "13px" }}>Set</button>
                <button onClick={() => printSection("twos")}
                  style={{ backgroundColor: "#6b7280", color: "white", border: "none", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "13px" }}>🖨 Print</button>
                <button onClick={clearTwos}
                  style={{ backgroundColor: "#dc2626", color: "white", border: "none", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "13px" }}>🔄 Clear</button>
              </div>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={hdr}>Player</th>
                  <th style={hdr}>Group</th>
                  <th style={{ ...hdr, textAlign: "center" }}>Paid</th>
                </tr>
              </thead>
              <tbody>
                {twos.length === 0 ? (
                  <tr><td colSpan={3} style={{ padding: "12px", textAlign: "center", color: "#9ca3af", fontSize: "13px" }}>No twos recorded yet</td></tr>
                ) : twos.map((p, i) => (
                  <tr key={p.player_id}>
                    <td style={row(i)}>{p.name}</td>
                    <td style={row(i)}>{p.grp || ""}</td>
                    <td style={{ ...row(i), textAlign: "center" }}>
                      <input type="checkbox" checked={!!paid[p.player_id]}
                        onChange={e => setPaid(prev => ({ ...prev, [p.player_id]: e.target.checked }))}
                        style={{ width: "16px", height: "16px", cursor: "pointer" }} />
                    </td>
                  </tr>
                ))}
                {[...Array(Math.max(0, 10 - twos.length))].map((_, i) => (
                  <tr key={`empty-${i}`}><td style={row(twos.length + i)}>&nbsp;</td><td style={row(twos.length + i)}></td><td style={row(twos.length + i)}></td></tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: "8px 12px", backgroundColor: "#f0fdf4", borderTop: "1px solid #d1d5db", color: "#16a34a", fontWeight: "600", fontSize: "13px" }}>
              Paid: {paidCount} / {twos.length} &nbsp; Total: £{twosTotal.toFixed(2)}
            </div>
          </div>

          {/* FINES */}
          <div style={{ flex: 1, minWidth: "300px", border: "1px solid #d1d5db", borderRadius: "8px", overflow: "hidden" }}>
            <div style={{ backgroundColor: "#f0fdf4", padding: "10px 12px", borderBottom: "1px solid #d1d5db", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
              <div style={{ fontWeight: "700", fontSize: "13px" }}>🎵 FINES — by group</div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                <button onClick={() => printSection("fines")}
                  style={{ backgroundColor: "#6b7280", color: "white", border: "none", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "13px" }}>🖨 Print</button>
                <button onClick={clearFines}
                  style={{ backgroundColor: "#dc2626", color: "white", border: "none", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "13px" }}>🔄 Clear</button>
              </div>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={hdr}>Group</th>
                  <th style={hdr}>Members</th>
                  <th style={{ ...hdr, textAlign: "center" }}>Total Fines (£)</th>
                </tr>
              </thead>
              <tbody>
                {sortedGroupNumbers.length === 0 ? (
                  <tr><td colSpan={3} style={{ padding: "12px", textAlign: "center", color: "#9ca3af", fontSize: "13px" }}>No groups set up yet</td></tr>
                ) : sortedGroupNumbers.map((g, i) => {
                  const f = groupFines[g] || { total: 0 };
                  return (
                    <tr key={g}>
                      <td style={{ ...row(i), fontWeight: "700" }}>{g || "-"}</td>
                      <td style={row(i)}>{groupMap[g].map(p => p.name).join(", ")}</td>
                      <td style={{ ...row(i), textAlign: "center" }}>
                        <input type="number" min="0" step="0.01" value={f.total || ""}
                          onChange={e => handleGroupFineChange(g, e.target.value)}
                          placeholder="0.00"
                          style={{ width: "80px", padding: "2px 4px", border: "1px solid #d1d5db", borderRadius: "4px", textAlign: "center", fontSize: "13px" }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ padding: "8px 12px", backgroundColor: "#f0fdf4", borderTop: "1px solid #d1d5db", color: "#dc2626", fontWeight: "600", fontSize: "13px" }}>
              Total Fines: £{totalFines.toFixed(2)}
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
}
